import React, { FC, useEffect } from 'react'
import { LinkProps } from 'next/link'

import clsx from 'clsx'
import Link from 'next/link'

import { ADMIN_SIDEBAR_DRAWER_WIDTH, ADMIN_SIDEBAR_DRAWER_WIDTH_CLOSED } from '@docpace/admin-react-components'
import adminRoutes from '@docpace/shared-react-routes/adminRoutes'
import { Divider } from '@docpace/shared-react-components/Divider'
import { Drawer } from '@docpace/shared-react-components/Drawer'

import {
    ChevronLeftIcon,
    ChevronRightIcon,
    PushpinPinnedIcon,
    PushpinUnpinnedIcon,
} from '@docpace/shared-react-icons'

export interface AdminSidebarProps {
    practiceId?: string
    providerId?: string
    
    isOpen: boolean
    isPinned: boolean
    isHovering?: boolean
    setIsSidebarPinned: (isSidebarPinned: boolean) => Promise<void>
    setIsSidebarOpen: (isSidebarOpen: boolean) => void
}

const ListItemText = ({
    primary,
    className,
}:{
    primary: string
    className: string
})=>{
    const style: any = { textWrap: 'nowrap' }
    return <div className={className} style={style}>
        {primary}
    </div>
}

export interface AdminSidebarLinkProps {
    title: string
    id: string
    Icon?: any
    linkProps?: LinkProps
    hideIcon?: boolean
    disabled?: boolean
    dense?: boolean
    endIcon?: any
    onClick?: any
}

const MuiSidebarNavListIcon = React.memo(
    Object.assign(
        (props: any) => {
            const { linkProps, disabled, Icon } = props
            const iconComponent = (
                <div className={'w-6 m-0 mr-2 text-gray-600 dark:text-gray-50'}>
                    <Icon />
                </div>
            )
            return linkProps && !disabled ? (
                <Link {...linkProps}>
                    {iconComponent}
                </Link>
            ) : (
                iconComponent
            )
        },
        { displayName: 'MuiSidebarNavListIcon' }
    )
)

const MuiSidebarNavListItemText = React.memo(
    Object.assign(
        (props: any) => {
            const { title, linkProps, disabled } = props
            const titleComponent = (
                <ListItemText
                    primary={title}
                    className={
                        'mx-0 my-4 font-sm text-gray-600 dark:text-gray-50 w-full'
                    }
                />
            )
            return linkProps && !disabled ? (
                <Link {...linkProps}>
                    {titleComponent}
                </Link>
            ) : (
                titleComponent
            )
        },
        { displayName: 'MuiSidebarNavListItemText' }
    )
)

export const AdminSidebarLink: FC<AdminSidebarLinkProps> = (props) => {
    const {
        linkProps,
        title,
        id,
        Icon,
        hideIcon,
        disabled,
        dense,
        endIcon,
        onClick,
    } = props
    return (
        <div
            onClick={onClick}
            key={title}
            id={id}
            className={clsx(
                'h-10 cursor-pointer flex items-center w-full', 
                hideIcon && 'm-0 p-0', 
                disabled && 'disabled', 
                dense && 'h-10',
            )}
        >
            {Icon && <MuiSidebarNavListIcon {...props} />}
            <MuiSidebarNavListItemText {...props} />
            {endIcon}
        </div>
    )
}

export const AdminSidebar: FC<AdminSidebarProps> = (props) => {
    const {
        practiceId,
        providerId,
        isPinned,
        isOpen,
        isHovering,
        setIsSidebarOpen,
        setIsSidebarPinned,
    } = props
    // const classes = useAdminSidebarStyles()
    // const theme = useTheme()

    const MenuTrayIcon = () => {
        // const isLtr = theme.direction === 'ltr'
        const isLtr = true
        const isChevronLeft = !(isLtr && isOpen) && (isLtr || isOpen)
        return (
            <button
                id='sidebar-tray-button'
                onClick={() => setIsSidebarOpen(!isOpen)}
                className='text-gray-600 dark:text-gray-50'
            >
                {isChevronLeft ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </button>
        )
    }

    const PushpinIcon = () => {
        const style = { fontSize: '0.9rem', right: 5 }
        return (
            <button className='text-gray-600 dark:text-gray-50'>
                {isPinned ? (
                    <PushpinPinnedIcon style={style} />
                ) : (
                    <PushpinUnpinnedIcon style={style} />
                )}
            </button>
        )
    }

    useEffect(() => {
        !isPinned && setIsSidebarOpen(!!isHovering)
    }, [isHovering, isPinned])

    if (!practiceId) return <></>

    return (
        <Drawer
            id={'admin-sidebar'}
            variant="permanent"
            className='flex flex-col h-screen !overflow-hidden'

            // className={clsx(classes.drawer, {
            //     [classes.drawerOpen]: isOpen,
            //     [classes.drawerClose]: !isOpen,
            // })}
            openWidth={ADMIN_SIDEBAR_DRAWER_WIDTH}
            closedWidth={ADMIN_SIDEBAR_DRAWER_WIDTH_CLOSED}
            isHovering={isHovering}
            isOpen={isOpen}
            // classes={{
            //     paper: clsx({
            //         [classes.drawerOpen]: isOpen,
            //         [classes.drawerClose]: !isOpen,
            //     }),
            // }}
        >
            <div className='flex-1 flex flex-col' style={{ width: ADMIN_SIDEBAR_DRAWER_WIDTH }}>
                <li className='w-full pt-4 list-none'>
                                    <div className='flex items-center ml-4 my-2'>
                                        <MenuTrayIcon />
                                    </div>
                                </li>
                                <Divider />
                <li className='ml-4 w-full flex-1 flex justify-start flex-col list-none'>
                    <AdminSidebarLink
                        title="Practice"
                        id='sidebar-practice-link'
                        linkProps={{
                            href: adminRoutes.PracticeHome.hrefNext({ practiceId }),
                        }}
                        Icon={adminRoutes.PracticeHome.icon}
                    />
                    <AdminSidebarLink
                        title="Appointment Types"
                        id='sidebar-appointment-types-link'
                        linkProps={{
                            href: adminRoutes.PracticeAppointmentTypes.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeAppointmentTypes.icon}
                    />
                    <AdminSidebarLink
                        title="Departments"
                        id='sidebar-departments-link'
                        linkProps={{
                            href: adminRoutes.PracticeDepartments.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeDepartments.icon}
                    />
                    <AdminSidebarLink
                        title="Providers"
                        id='sidebar-providers-link'
                        linkProps={{
                            href: adminRoutes.PracticeProviders.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeProviders.icon}
                    />
                    {/* <AdminSidebarLink disabled title='Devices' linkProps={{ href: adminRoutes.PracticeDevices.hrefNext({ practiceId }) }} Icon={adminRoutes.PracticeDevices.icon}/> */}
                    <AdminSidebarLink
                        title="Users"
                        id='sidebar-managers-link'
                        linkProps={{
                            href: adminRoutes.PracticeManagers.hrefNext({ practiceId }),
                        }}
                        Icon={adminRoutes.PracticeManagers.icon}
                    />
                    <AdminSidebarLink
                        title="Translations"
                        id='sidebar-translations-link'
                        linkProps={{
                            href: adminRoutes.PracticeTranslations.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeTranslations.icon}
                    />
                    {/* <AdminSidebarLink
                        title="Reports"
                        id='sidebar-reports-link'
                        linkProps={{
                            href: adminRoutes.PracticeReportsHome.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeReportsHome.icon}
                    /> */}
                    {/* <AdminSidebarLink
                        title="Configure"
                        id='sidebar-configure-link'
                        linkProps={{
                            href: adminRoutes.PracticeHome.hrefNext({
                                practiceId,
                            }),
                        }}
                        Icon={adminRoutes.PracticeConfigure.icon}
                    /> */}
                </li>
            </div>
            <li className='flex flex-col justify-end items-start' style={{ width: ADMIN_SIDEBAR_DRAWER_WIDTH }}>
                <div style={{ position: 'relative', right: -20, top: -8 }}>
                    <AdminSidebarLink
                        title={`${isPinned ? 'Unpin' : 'Pin'} Sidebar`}
                        id='sidebar-pushpin-button'
                        Icon={PushpinIcon}
                        onClick={() => setIsSidebarPinned(!isPinned)}
                    />
                </div>
            </li>
        </Drawer>
    )
}
